<template>
  <div class="section container">
    <div class="section-header">
      <div class="section-title">
        <div class="section-icon section-icon-email" :class="brandClass()"></div>
        <div class="section-title-label">
          {{$t("profile_personal_data_email_summary_title")}}
        </div>
        <div class="section-subtitle-label">
          {{ $t("profile_personal_data_email_summary_subtitle") }}
        </div>
      </div>

      <!-- Edit section button -->
      <div
        class="section-edit pointer"
        :class="{ hidden: editDisabled }"
        @click="editHandler()"
        @keydown="ariaClick"
        role="button"
        tabindex="0"
      >
        <div class="section-edit-icon"></div>
        <div class="section-edit-label">
          {{ $t("profile_edit_button") }}
        </div>
      </div>
    </div>
    
    <div class="infos email-information">
      <div class="info-container">
        <div class="info email">

          <!-- BMW ID label -->
          <div class="label email-label">
            {{ $t("profile_your_bmw_id_label") }} ({{ getBmwIdEmailTypeTranslation() }})
          </div>

          <!-- BMW ID value -->
          <div class="value email-value">
            {{ bmwID }}
          </div>
        </div>
      </div>
    </div>

    <!-- If the user has at least one other email set as not to be deleted... -->
    <div 
      v-if="otherUserMailsNotToDelete().length" 
      class="infos email-information further-emails-information">

      <div class="info-container">
        <div class="info email">
          <div class="label email-label">

            <!-- Further emails label -->
            <span id="profile-emails-further-emails-label">
              {{ $t("profile_further_emails_label") }}
            </span>
            <OneTippy
              :name="'profile-emails-further-emails-label-tooltip'"
              :html="$t('profile_further_emails_info')"
              :placement="'top-start'"
              :maxWidth="500"
              :labelledBy="'profile-emails-further-emails-label'"
              :brandClass="brandClass()"
            />
          </div>

          <!-- For each other user email... -->
          <div
            v-for="(otherUserMailObject, otherUserMailObjectIndex) in otherUserMailsNotToDelete()"
            :key="otherUserMailObjectIndex"
            class="further-emails-email-container">

            <!-- If the email is set as NOT to delete... -->
            <template v-if="!otherUserMailObject.userMail.delete">

              <!-- Email value -->
              <div class="value email-value">
                {{ otherUserMailObject.userMail.value }}
              </div>

              <!-- Delete button -->
              <div 
                v-if="!isCurrentPersonalID(otherUserMailObject.userMail.value)" 
                @keydown="ariaClick" 
                role="button" 
                tabindex="0" 
                class="delete" 
                :class="{
                  'pointer' : (!addMode && editMode==-1), 
                  'hidden' : editDisabled
                }" 
                @click="(!addMode && editMode==-1) && openPopup(otherUserMailObject.userMailIndex)">
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Horizontal rule -->
    <hr>

    <!-- Delete email alert -->
    <OneAlert
      :brandClass="brandClass()"
      v-if="showPopup"
      :title="this.$t('profile_personal_data_delete_email_title')"
      :content="this.$t('profile_personal_data_delete_email_subtitle')"
      :button_confirm_label="this.$t('profile_personal_data_delete_email_confirm_button')"
      :button_cancel_label="this.$t('profile_personal_data_delete_email_cancel_button')"
      @confirm="remove"
      @close="closePopup"
    />
  </div>
</template>
<style lang="scss" scoped>
  .email-message{
    padding: 10px 0;
    color: $grey;
    position: relative;
  }
  .form{
    margin-top: 22px;
  }
  .buttonsContainer{
    margin-top: 40px;
  }
  .further-emails-information {
    .further-emails-email-container {
      display: flex;
      align-items: center;

      .email-value {
        // TODO: maybe this class needs to have a custom implementation for each brand
        color: #BBBBBB;
        width: min-content;
        margin-right: 15px !important;
        // 30px is icon width with a bit more space
        // this calc() is used to prevent the icon from going out of the parent div in case the email is very long
        max-width: calc(100% - 30px);
      }
      .delete {
        margin-top: 7px;
        position: static !important;
      }
    }
  }
</style>
<script>
import useVuelidate from '@vuelidate/core'
import {
  required,
  email
} from "@vuelidate/validators";
import OneTippy from "../../../../gcdm-one-library/gcdm-one-tippy/src/OneTippy";
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";


export default {
  inject:['$c', '$t', 'brandClass'],
  setup() {
    return { v$: useVuelidate() }
  },
  name: "ProfileEmails",
  //validations from vuelidate
  mixins: [oneUtils.customValidationMixin,oneUtils.accessibilityMixin],
  props: {
    sectionContent: {
      type: String,
      default: oneUtils.enums.profilePages.PERSONAL_DATA,
    },
    user: {},
    editDisabled: {
      default: false,
      type: Boolean,
    },
  },
  components: {
    OneTippy
  },
  data: () => ({
    form: {
      email: null,
      emailType: null,
    },
    // typeIndex: null,
    // showSelect: false,
    editMode: -1,
    preferredEmail:0,
    addMode: false,
    showPopup: false,
    indexToRemove: -1,
  }),
  //vuelidate custom validations
  validations: {
    form: {
      email: {
        required,
        email,
      },
      emailType: {
        required,
      },
    },
  },
  computed: {
    isButtonDisabled: function () {
      return (
        this.v$.$invalid
      );
    },
    /**
     * Return the user's emails
     */
    userMail: function(){
      return this.user.communications ? this.user.communications.communicationEMails : [];
    },
    /**
     * Returns the
     * @returns {null|string}
     */
    bmwID: function() {
      if (this.userMail.length === 0) return null;
      const contact = this.userMail.find(e => e.value.toLowerCase() === this.user.email.toLowerCase());
      if (contact) return contact.value;
      else return null;
    },
    bmwIDType: function() {
      if (this.userMail.length === 0) return null;
      const contact = this.userMail.find(e => e.value.toLowerCase() === this.user.email.toLowerCase());
      if (contact) return contact.communicationStatus;
      else return null;
    }
  },
  mounted() {
    this.checkPreferredIndex();
  },
  updated() {
    this.checkPreferredIndex();
    this.$emit('isEditMode', {subSection:'PE', value:this.editMode!=-1 || this.addMode});
  },
  methods: {
    /**
     * Handles edit click: will redirect to account data section > profile personal id (edit mode)
     */
    editHandler() {
      oneUtils.utils.setComponentAction("account_data-profile_personal_id-is_edit_mode");
      
      const pageKey = oneUtils.enums.profilePages.ACCOUNT_DATA;

      if(!window['$startupwebcomponent']) {
        window.localStorage.setItem("oneid_section", pageKey);
      }
      this.$emit("update:sectionContent", pageKey);
    },
    /**
     * This function check the preferred email from the user's comunication email
     */
    checkPreferredIndex(){
      if(this.user.communications && this.user.communications.communicationEMails){
        for (let index = 0; index < this.user.communications.communicationEMails.length; index++) {
          if(this.user.communications.communicationEMails[index].preferred && !this.user.communications.communicationEMails[index].delete){
            this.preferredEmail = index;
          }
        }
      }
    },
    /**
     * This function open the edit mode
     */
    editClicked(h){
      //Tracy tracking
      this.handleEditMode(h);
    },
    /**
     * This function close the edit mode
     */
    cancelEditClicked(){
      //Tracy tracking
      this.handleEditMode(-1);
    },
    /**
     * Tracy tracking and handle add more email management
     */
    addOrCancelAddClicked(){
      this.handleAddMode();
    },
    /**
     * add more email management and form reset value
     */
    handleAddMode(){
      this.addMode = !this.addMode;
      this.form = {
        email: null,
        emailType:null
      };
      this.v$.$reset();
    },
    /**
     * check the user email's presence 
     */
    isCurrentPersonalID: function(formValue){
      return formValue && this.user.email && formValue.trim().toLowerCase() == this.user.email.trim().toLowerCase();
    },
    /**
     * Return the user's emails other than accountid
     */
    otherUserMails: function(){
      let otherUserMails = [];

      this.userMail.forEach((userMail, userMailIndex) => {
        if(userMail.value.toLowerCase() !== this.user.email.toLowerCase()) {
          let otherUserMail = {
            userMail: userMail,
            userMailIndex: userMailIndex
          };

          otherUserMails.push(otherUserMail);
        }
      });

      return otherUserMails;
    },
    /**
     * tReturn the user's emails other than accountid hat are not set as to be deleted
     */
    otherUserMailsNotToDelete: function(){
      const otherUserMails = this.otherUserMails();

      let otherUserMailsNotToDelete = [];

      otherUserMailsNotToDelete = otherUserMails.filter(otherUserMailObject => !otherUserMailObject.userMail.delete)

      return otherUserMailsNotToDelete;
    },
    /**
     * Return the user's emails type
     */
    /* getEmailInfo(index){
      return this.$t(
        this.$c("list_of_emailStatus").filter(type =>
          type.typeKey == this.user.communications.communicationEMails[index].communicationStatus
        )[0].translationKey
      );
    }, */
    getBmwIdEmailTypeTranslation(){
      let type = this.$c("list_of_emailStatus").find(type => type.typeKey == this.bmwIDType);

      if(type)
        return this.$t(type?.translationKey);
      else
        return null;
    },
    /**
     * Hide the delete popup email and update (add,edit or remove) the user's emails
     */
    remove: function (){
      this.showPopup = false;
      this.submitUpdate();
    },
    /**
     * Hide the delete popup email
     */
    openPopup(index){
      this.indexToRemove = index;
      this.showPopup = true;
    },
    /**
     * Show the delete popup email
     */
    closePopup(){
      this.showPopup = false;
    },
    /**
     *  Update (add, edit or remove) the user's email
     */
    submitUpdate() {
      oneUtils.utils.showLeftLoader();
      if(!this.user.communications){
        this.user.communications = {
          communicationEMails:[]
        };
      }else if(!this.user.communications.communicationEMails){
        this.user.communications.communicationEMails = [];
      }
      if (this.user.communications.communicationEMails[this.editMode]){
        //edit
        const oldEmail = this.user.communications.communicationEMails[this.editMode];
        oldEmail.value = this.form.email;
        oldEmail.communicationStatus = this.form.emailType;
        this.handleEditMode(-1);
      }else if(this.addMode){
        //add
        this.user.communications.communicationEMails.push({
          "communicationStatus": this.form.emailType,
          "communicationType": 'EMAIL',
          "preferred": false,
          "value": this.form.email,
        });

        //set indexOf preferred mail if 1 mail
        const emailNotDeleted = this.user.communications.communicationEMails.filter(email=>!email.delete);
        if (emailNotDeleted.length==1){
          this.user.communications.communicationEMails[this.user.communications.communicationEMails.indexOf(emailNotDeleted[0])].preferred=true;
        }
        this.handleAddMode();
      }else{
        //remove
        this.manageRemove();
      }
      this.checkPreferredIndex();
    },
    /**
     *  Open edit mode
     */
    handleEditMode(key){
      this.editMode = key;
      if(this.editMode!=-1){
        this.prefillForm(key);
      }
    },
    /**
     *  Form valorization
     */
    prefillForm: function (key) {
      // if (this.user.communications && this.user.communications.communicationEMails && this.typeKeys.includes(this.user.communications.communicationEMails[key].communicationStatus)) {
      //   const typeIndex = this.typeKeys.indexOf(this.user.communications.communicationEMails[key].communicationStatus);
      //   this.setType({
      //     value: this.typeValues[typeIndex],
      //     index: typeIndex,
      //   });
      //   this.touchForm("emailType");
      // }

      if (this.user.communications && this.user.communications.communicationEMails) {
        const type = this.user.communications.communicationEMails[key].communicationStatus;
        this.form.emailType = this.getSelectOptions("list_of_emailStatus").some(
            (o) => o.value === type
        )
            ? type
            : null;
        this.touchForm("emailType");
      }

      this.form.email = this.user.communications.communicationEMails[key].value;
      this.touchForm("email");

    },
    /**
     *  Delete emails management
     */
    manageRemove(){
      // If the email to be deleted is the original user email
      if(this.$store.getters.getOriginalUser.communications?.communicationEMails?.[this.indexToRemove]){
        this.user.communications.communicationEMails[this.indexToRemove]['delete']=true;
        //set indexOf preferred mail to first not deleted
        const emailNotDeleted = this.user.communications.communicationEMails.filter(email=>!email.delete);
        if(emailNotDeleted.length>0 && this.user.communications.communicationEMails[this.indexToRemove].preferred){
          this.user.communications.communicationEMails[this.user.communications.communicationEMails.indexOf(emailNotDeleted[0])].preferred=true;
        }
        //needed for watch
        const email = this.user.communications.communicationEMails[this.indexToRemove]['value'];
        this.user.communications.communicationEMails[this.indexToRemove]['value']="";
        this.user.communications.communicationEMails[this.indexToRemove]={
          'communicationStatus':this.user.communications.communicationEMails[this.indexToRemove].communicationStatus,
          'communicationType':this.user.communications.communicationEMails[this.indexToRemove].communicationType,
          'preferred':this.user.communications.communicationEMails[this.indexToRemove].preferred,
          'consGUID':this.user.communications.communicationEMails[this.indexToRemove].consGUID,
          'delete':true,
          'value':email
        };
      }else{
        this.user.communications.communicationEMails.splice(this.indexToRemove, 1);
        const emailNotDeleted = this.user.communications.communicationEMails.filter(email=>!email.delete);
        if(emailNotDeleted.length){
          this.user.communications.communicationEMails[this.user.communications.communicationEMails.indexOf(emailNotDeleted[0])].preferred=true;
        }
      }
    },
    /**
     *  Form submit
     */
    validateEmail() {
      this.submitUpdate();
    },
    /**
     *  Setting preferred email
     */
    handlePreferredEmail(key){
      this.preferredEmail = key;
      for (let index = 0; index < this.user.communications.communicationEMails.length; index++) {
        this.user.communications.communicationEMails[index].preferred=false;
        this.user.communications.communicationEMails[this.preferredEmail].preferred=true;
      }
    }
  },
};
</script>
